import { useEffect, useState } from "react";
import api, { formatApiError, downloadFile, previewFile } from "@/lib/api";
import { formatDate } from "@/lib/format";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { StatusBadge } from "@/components/StatusBadge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Upload, Download, Eye, CalendarCheck } from "lucide-react";
import { toast } from "sonner";

const STATUS_TONE = { pending: "pending", approved: "active", overdue: "overdue" };
const STATUS_LABEL = { pending: "Onay bekliyor", approved: "Onaylandı", overdue: "Süresi geçti" };
const MONTHS_TR = ["", "Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran", "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"];
const thisYear = new Date().getFullYear();

export default function VisitReports({ firmId, canEdit }) {
  const [year, setYear] = useState(thisYear);
  const [months, setMonths] = useState(null);
  const [dlg, setDlg] = useState(null); // month obj
  const [form, setForm] = useState({});
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const [years, setYears] = useState([thisYear]);
  useEffect(() => {
    api.get(`/firms/${firmId}/years`).then(({ data }) => {
      const ys = data.years && data.years.length ? data.years : [thisYear];
      setYears(ys);
      if (!ys.includes(year)) setYear(ys[0]);
    }).catch(() => {});
  }, [firmId]); // eslint-disable-line

  const load = async () => {
    try { const { data } = await api.get(`/firms/${firmId}/visit-reports?year=${year}`); setMonths(data.months); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, [firmId, year]); // eslint-disable-line

  const openDlg = (m) => {
    setDlg(m);
    setForm({ status: m.status || "pending", actual_date: m.actual_date || "", planned_date: m.planned_date || "", is_signed_final: !!m.has_final, publish: !!m.pdf?.published });
    setFile(null);
  };

  const submit = async () => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("year", String(year));
      fd.append("month", String(dlg.month));
      fd.append("status", form.status);
      if (form.planned_date) fd.append("planned_date", form.planned_date);
      if (form.actual_date) fd.append("actual_date", form.actual_date);
      fd.append("is_signed_final", String(!!form.is_signed_final));
      fd.append("publish", String(!!form.publish));
      if (file) fd.append("file", file);
      await api.post(`/firms/${firmId}/visit-reports`, fd);
      toast.success("Ziyaret raporu kaydedildi.");
      setDlg(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (months === null) return <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-sky-600" /></div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Label className="text-sm">Yıl</Label>
        <Select value={String(year)} onValueChange={(v) => setYear(parseInt(v, 10))}>
          <SelectTrigger className="w-28" data-testid="visit-year-select"><SelectValue /></SelectTrigger>
          <SelectContent>{years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        {months.map((m) => (
          <Card key={m.month} className="border-slate-200" data-testid={`visit-month-${m.month}`}>
            <CardContent className="py-3 flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <CalendarCheck className="h-5 w-5 text-sky-500 shrink-0" />
                <div>
                  <div className="font-medium text-sm text-slate-800">{m.month_name} {m.year}</div>
                  <div className="text-xs text-slate-500">
                    {m.actual_date ? `Ziyaret: ${formatDate(m.actual_date)}` : "Ziyaret tarihi girilmedi"}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {m.status ? <StatusBadge tone={STATUS_TONE[m.status]}>{m.status_label}</StatusBadge> : <StatusBadge tone="muted">Kayıt yok</StatusBadge>}
                {m.has_report ? (m.has_final ? <StatusBadge tone="active">Nihai PDF var</StatusBadge> : <StatusBadge tone="pending">Bekleyen PDF</StatusBadge>) : <StatusBadge tone="overdue">PDF yok</StatusBadge>}
                {m.pdf && <>
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => previewFile(`/documents/${m.pdf.id}/download`)} data-testid={`visit-preview-${m.month}`} title="Önizle"><Eye className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => downloadFile(`/documents/${m.pdf.id}/download`, `${m.pdf.name}.pdf`)} title="İndir"><Download className="h-4 w-4" /></Button>
                </>}
                {canEdit && <Button variant="outline" size="sm" onClick={() => openDlg(m)} data-testid={`visit-edit-${m.month}`}><Upload className="h-3.5 w-3.5 mr-1" /> {m.has_report ? "Güncelle" : "Rapor Yükle"}</Button>}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display">{dlg ? `${dlg.month_name || MONTHS_TR[dlg.month]} ${year}` : ""} Ziyaret Raporu</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Ziyaret Tarihi (gerçekleşen)</Label>
              <Input type="date" value={form.actual_date} onChange={(e) => setForm({ ...form, actual_date: e.target.value })} data-testid="visit-actual-date" /></div>
            <div className="space-y-1.5"><Label>E-devlet Durumu</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="visit-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Onay bekliyor</SelectItem>
                  <SelectItem value="approved">Onaylandı</SelectItem>
                  <SelectItem value="overdue">Süresi geçti</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.status === "overdue" && (
              <label className="flex items-center gap-2 text-sm" data-testid="visit-signed-wrap">
                <Checkbox checked={form.is_signed_final} onCheckedChange={(v) => setForm({ ...form, is_signed_final: !!v })} data-testid="visit-signed-final" />
                Firma ve danışman tarafından imzalı/taratılmış son nüsha
              </label>
            )}
            <div className="space-y-1.5">
              <Label>Rapor PDF'i {dlg?.has_report && "(yeni sürüm)"}</Label>
              <Input type="file" accept=".pdf" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="visit-file-input" />
              <p className="text-[11px] text-slate-400">Raporun ait olduğu ay yükleme tarihinden bağımsızdır. İçerik değiştirilmez.</p>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={form.publish} onCheckedChange={(v) => setForm({ ...form, publish: !!v })} data-testid="visit-publish" />
              Müşteriye yayımla
            </label>
          </div>
          <DialogFooter>
            <Button className="bg-sky-600 hover:bg-sky-700" onClick={submit} disabled={busy} data-testid="visit-save">{busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />} Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
